import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  HomeIcon, 
  UsersIcon, 
  PlusIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  UserCircleIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  CreditCardIcon,
  ChartBarIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  WrenchScrewdriverIcon,
  CodeBracketIcon,
  ChatBubbleOvalLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';

const Layout = ({ children }) => {
  const location = useLocation();
  const [whatsappAgentsMenuOpen, setWhatsappAgentsMenuOpen] = useState(true);
  const [aiAgentsMenuOpen, setAiAgentsMenuOpen] = useState(false);
  const [billingMenuOpen, setBillingMenuOpen] = useState(false);
  const [requestsMenuOpen, setRequestsMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showTrialBanner, setShowTrialBanner] = useState(false);
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard/', icon: HomeIcon },
  ];

  const bottomNavigation = [
    { name: 'User Management', href: '/dashboard/users', icon: UserGroupIcon },
    { name: 'Audit Trails', href: '/dashboard/audit-trails', icon: DocumentTextIcon },
    { name: 'My Profile', href: '/dashboard/profile', icon: UserCircleIcon },
    { name: 'API Documentation', href: '/dashboard/docs', icon: CodeBracketIcon },
    { name: 'Contact Support', href: '/dashboard/contact-support', icon: ChatBubbleOvalLeftEllipsisIcon },
  ];

  const whatsappAgentsSubMenu = [
    { name: 'My Connections', href: '/dashboard/connections', icon: UsersIcon },
    { name: 'Create Agent', href: '/dashboard/agents/create', icon: PlusIcon },
  ];

  const aiAgentsSubMenu = [
    { name: 'My Agents', href: '/dashboard/agents', icon: SparklesIcon },
    { name: 'Train Agent', href: '/dashboard/agents/train', icon: PlusIcon },
    { name: 'Add Tools', href: '/dashboard/agents/tools', icon: WrenchScrewdriverIcon },
  ];

  const billingSubMenu = [
    { name: 'Subscription', href: '/dashboard/billing/subscription', icon: CreditCardIcon },
    { name: 'Usage & Analytics', href: '/dashboard/billing/usage', icon: ChartBarIcon },
    { name: 'Billing History', href: '/dashboard/billing/history', icon: CogIcon },
  ];

  const requestsSubMenu = [
    {
      name: 'Chat',
      subItems: [
        { name: 'Send Text Message', href: '/dashboard/requests/chat/text', icon: ChatBubbleLeftRightIcon },
        { name: 'Send Group Message', href: '/dashboard/requests/chat/group', icon: UserGroupIcon },
        { name: 'Send Voice Message', href: '/dashboard/requests/chat/voice', icon: SignalIcon },
        { name: 'Send Poll', href: '/dashboard/requests/chat/poll', icon: ChatBubbleLeftRightIcon },
        { name: 'Send Location', href: '/dashboard/requests/chat/location', icon: ChatBubbleLeftRightIcon },
        { name: 'Create Channel', href: '/dashboard/requests/chat/channel', icon: MegaphoneIcon },
        // { name: 'Respond to Message', href: '/dashboard/requests/chat/respond', icon: ChatBubbleLeftRightIcon },
        // { name: 'Send Video/Image', href: '/dashboard/requests/chat/media', icon: ChatBubbleLeftRightIcon },
        // { name: 'Send File', href: '/dashboard/requests/chat/file', icon: ChatBubbleLeftRightIcon },
      ]
    },
    {
      name: 'Forwarding',
      subItems: [
        { name: 'Configure Forward URL', href: '/dashboard/requests/forward-url', icon: ArrowTopRightOnSquareIcon },
      ]
    },
    // {
    //   name: 'Status',
    //   subItems: [
    //     { name: 'Send Text Status', href: '/dashboard/requests/status/text', icon: SignalIcon },
    //     { name: 'Send Image Status', href: '/dashboard/requests/status/image', icon: SignalIcon },
    //     { name: 'Send Voice Status', href: '/dashboard/requests/status/voice', icon: SignalIcon },
    //   ]
    // }
  ];

  // const trainingSubMenu = [ // Commented out - training menu disabled
  //   { name: 'Agent Training', href: '/dashboard/agents', icon: CpuChipIcon },
  //   { name: 'Train Agent', href: '/dashboard/agents/create', icon: PlusIcon },
  // ];

  const isActive = (href) => {
    if (href === '/dashboard/') {
      return location.pathname === '/dashboard/' || location.pathname === '/dashboard';
    }
    // Fix for specific client routes
    if (href === '/dashboard/clients') {
      return location.pathname === '/dashboard/clients' || location.pathname.match(/^\/dashboard\/clients\/[^/]+$/);
    }
    if (href === '/dashboard/connections') {
      return location.pathname === '/dashboard/connections' || location.pathname.match(/^\/dashboard\/connections\/[^/]+$/);
    }
    if (href === '/dashboard/clients/create') {
      return location.pathname === '/dashboard/clients/create';
    }
    // Fix for specific agent routes
    if (href === '/dashboard/agents') {
      return location.pathname === '/dashboard/agents' || 
             (location.pathname.match(/^\/dashboard\/agents\/[^/]+$/) && 
              location.pathname !== '/dashboard/agents/create' &&
              location.pathname !== '/dashboard/agents/train');
    }
    if (href === '/dashboard/agents/create') {
      return location.pathname === '/dashboard/agents/create';
    }
    return location.pathname.startsWith(href);
  };

  const isWhatsappAgentsActive = () => {
    return location.pathname.startsWith('/dashboard/connections') || 
           location.pathname === '/dashboard/agents/create';
  };

  const isAiAgentsActive = () => {
    return (location.pathname === '/dashboard/agents' || 
            location.pathname.match(/^\/dashboard\/agents\/[^/]+$/) ||
            location.pathname === '/dashboard/agents/train') &&
           location.pathname !== '/dashboard/agents/create';
  };

  const isBillingActive = () => {
    return location.pathname.startsWith('/dashboard/billing');
  };

  const isRequestsActive = () => {
    return location.pathname.startsWith('/dashboard/requests');
  };

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await api.getSubscriptionStatus();
        const widgetData = response.data.widget;
        setSubscriptionStatus(widgetData);
        
        // Show trial banner if user needs to upgrade
        // Check if payment is required based on access information
        if (widgetData.access?.requires_payment === true) {
          setShowTrialBanner(true);
        }
        // Also check for expired trials
        else if (widgetData.subscription?.status === 'trial' && 
                 widgetData.trial?.days_remaining <= 0) {
          setShowTrialBanner(true);
        }
        // Check for explicitly expired subscriptions
        else if (widgetData.subscription?.status === 'expired' || widgetData.subscription?.status === 'none') {
          setShowTrialBanner(true);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    
    fetchSubscriptionStatus();
  }, []);

  // const isTrainingActive = () => { // Commented out - training menu disabled
  //   return location.pathname.startsWith('/dashboard/agents');
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          
          {/* Mobile sidebar */}
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            {/* Mobile sidebar content */}
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <img src={`${process.env.PUBLIC_URL}/Snwolley_Logo.png`} alt="Snwolley Logo" className="h-8 w-8" />
                <span className="ml-3 text-lg font-bold text-gray-900">Snwolley</span>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {/* Regular navigation items */}
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-whatsapp-100 text-whatsapp-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="mr-3 flex-shrink-0 h-6 w-6" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* WhatsApp Agents Menu with Submenu */}
                <div className="space-y-1">
                  <button
                    onClick={() => setWhatsappAgentsMenuOpen(!whatsappAgentsMenuOpen)}
                    className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                      isWhatsappAgentsActive()
                        ? 'bg-whatsapp-100 text-whatsapp-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                      WhatsApp Agents
                    </div>
                    {whatsappAgentsMenuOpen ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>

                  {/* Submenu */}
                  {whatsappAgentsMenuOpen && (
                    <div className="ml-8 space-y-1">
                      {whatsappAgentsSubMenu.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive(item.href)
                                ? 'bg-whatsapp-100 text-whatsapp-900'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                          >
                            <Icon className="mr-3 flex-shrink-0 h-5 w-5" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* AI Agents Menu with Submenu */}
                <div className="space-y-1">
                  <button
                    onClick={() => setAiAgentsMenuOpen(!aiAgentsMenuOpen)}
                    className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                      isAiAgentsActive()
                        ? 'bg-whatsapp-100 text-whatsapp-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <SparklesIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                      AI Agents
                    </div>
                    {aiAgentsMenuOpen ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>

                  {/* Submenu */}
                  {aiAgentsMenuOpen && (
                    <div className="ml-8 space-y-1">
                      {aiAgentsSubMenu.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive(item.href)
                                ? 'bg-whatsapp-100 text-whatsapp-900'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                          >
                            <Icon className="mr-3 flex-shrink-0 h-5 w-5" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Billing & Subscription Menu */}
                <div className="space-y-1">
                  <button
                    onClick={() => setBillingMenuOpen(!billingMenuOpen)}
                    className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                      isBillingActive()
                        ? 'bg-green-100 text-green-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <CreditCardIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                      Billing & Usage
                      {subscriptionStatus?.subscription_status === 'trial' && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                          Trial
                        </span>
                      )}
                    </div>
                    {billingMenuOpen ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>

                  {/* Submenu */}
                  {billingMenuOpen && (
                    <div className="ml-8 space-y-1">
                      {billingSubMenu.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive(item.href)
                                ? 'bg-green-100 text-green-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Icon className="mr-3 flex-shrink-0 h-5 w-5" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Manual Requests Menu - Blocked for Trial and Expired Users */}
                {(!subscriptionStatus?.access?.has_access ||
                  subscriptionStatus?.subscription.status === 'none') ? (
                  <div className="space-y-1">
                    <div className="w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-400 cursor-not-allowed">
                      <div className="flex items-center">
                        <ChatBubbleLeftRightIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                        Actions
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                          Premium
                        </span>
                      </div>
                    </div>
                    <div className="ml-8 px-2 py-2 text-xs text-gray-500 bg-gray-50 rounded-md">
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0 w-1 h-1 bg-gray-400 rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium text-gray-600">Premium Feature</p>
                          <p className="text-gray-500 mt-1">Upgrade to send messages directly</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <button
                      onClick={() => setRequestsMenuOpen(!requestsMenuOpen)}
                      className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                        isRequestsActive()
                          ? 'bg-green-100 text-green-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <div className="flex items-center">
                        <ChatBubbleLeftRightIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                        Actions
                      </div>
                      {requestsMenuOpen ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>

                    {/* Submenu */}
                    {requestsMenuOpen && (
                      <div className="ml-8 space-y-1">
                        {requestsSubMenu.map((category) => (
                          <div key={category.name} className="space-y-1">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1">
                              {category.name}
                            </div>
                            {category.subItems.map((item) => {
                              const Icon = item.icon;
                              return (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  onClick={() => setSidebarOpen(false)}
                                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                                    isActive(item.href)
                                      ? 'bg-green-100 text-green-800 border-l-2 border-green-500'
                                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                  }`}
                                >
                                  <Icon className="mr-3 flex-shrink-0 h-4 w-4" />
                                  {item.name}
                                </Link>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Bottom navigation items */}
                {bottomNavigation.map((item) => {
                  const Icon = item.icon;
                  
                  // Block API Documentation for trial, expired, and spark users
                  const isExpired = (subscriptionStatus?.subscription?.expires_at && 
                                   new Date(subscriptionStatus.subscription.expires_at) < new Date()) ||
                                  (subscriptionStatus?.subscription?.trial_expires_at && 
                                   new Date(subscriptionStatus.subscription.trial_expires_at) < new Date());
                  const isBlocked = subscriptionStatus?.subscription?.status === 'trial' || 
                                  isExpired ||
                                  subscriptionStatus?.subscription?.package_name?.toLowerCase() === 'spark';
                  if (item.name === 'API Documentation' && isBlocked) {
                    return (
                      <div
                        key={item.name}
                        className="group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-400 cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          <Icon className="mr-3 flex-shrink-0 h-6 w-6" />
                          {item.name}
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                            Premium
                          </span>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-whatsapp-100 text-whatsapp-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="mr-3 flex-shrink-0 h-6 w-6" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            
            {/* Mobile user info */}
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center">
                <UserCircleIcon className="h-8 w-8 text-whatsapp-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="ml-auto p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white shadow-lg">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <img src={`${process.env.PUBLIC_URL}/Snwolley_Logo.png`} alt="Snwolley Logo" className="h-8 w-8" />
            <span className="ml-3 text-xl font-bold text-gray-900">Snwolley</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {/* Regular navigation items */}
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-whatsapp-50 text-whatsapp-700 border-r-2 border-whatsapp-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}

            {/* WhatsApp Agents Menu with Submenu */}
            <div className="space-y-1">
              <button
                onClick={() => setWhatsappAgentsMenuOpen(!whatsappAgentsMenuOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isWhatsappAgentsActive()
                    ? 'bg-whatsapp-50 text-whatsapp-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                  WhatsApp Agents
                </div>
                {whatsappAgentsMenuOpen ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>

              {/* Submenu */}
              {whatsappAgentsMenuOpen && (
                <div className="ml-6 space-y-1">
                  {whatsappAgentsSubMenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-whatsapp-100 text-whatsapp-800 border-l-2 border-whatsapp-500'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* AI Agents Menu with Submenu */}
            <div className="space-y-1">
              <button
                onClick={() => setAiAgentsMenuOpen(!aiAgentsMenuOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isAiAgentsActive()
                    ? 'bg-whatsapp-50 text-whatsapp-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <SparklesIcon className="h-5 w-5 mr-3" />
                  AI Agents
                </div>
                {aiAgentsMenuOpen ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>

              {/* Submenu */}
              {aiAgentsMenuOpen && (
                <div className="ml-6 space-y-1">
                  {aiAgentsSubMenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-whatsapp-100 text-whatsapp-800 border-l-2 border-whatsapp-500'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Billing & Subscription Menu - Desktop */}
            <div className="space-y-1">
              <button
                onClick={() => setBillingMenuOpen(!billingMenuOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isBillingActive()
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-3" />
                  Billing & Usage
                  {subscriptionStatus?.subscription_status === 'trial' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                      Trial
                    </span>
                  )}
                </div>
                {billingMenuOpen ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>

              {/* Submenu */}
              {billingMenuOpen && (
                <div className="ml-6 space-y-1">
                  {billingSubMenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? 'bg-green-100 text-green-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Manual Requests Menu - Desktop - Blocked for Trial Users */}
            {subscriptionStatus?.subscription?.status === 'trial' ? (
              <div className="space-y-1">
                <div className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed">
                  <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                    Actions
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                      Premium
                    </span>
                  </div>
                  {/*<div className="flex items-center space-x-1">*/}
                  {/*  <span className="text-xs text-gray-400">Upgrade Required</span>*/}
                  {/*  <ChevronRightIcon className="h-4 w-4" />*/}
                  {/*</div>*/}
                </div>
                <div className="ml-6 px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0 w-1 h-1 bg-gray-400 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-600">Premium Feature</p>
                      <p className="text-gray-500 mt-1">Upgrade to send messages directly</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <button
                  onClick={() => setRequestsMenuOpen(!requestsMenuOpen)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isRequestsActive()
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                    Actions
                  </div>
                  {requestsMenuOpen ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>

                {/* Submenu */}
                {requestsMenuOpen && (
                  <div className="ml-6 space-y-1">
                    {requestsSubMenu.map((category) => (
                      <div key={category.name} className="space-y-1">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1">
                          {category.name}
                        </div>
                        {category.subItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                isActive(item.href)
                                  ? 'bg-green-100 text-green-800 border-l-2 border-green-500'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                              }`}
                            >
                              <Icon className="h-4 w-4 mr-3" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bottom navigation items */}
            {bottomNavigation.map((item) => {
              const Icon = item.icon;
              
              // Block API Documentation for trial, expired, and spark users
              const isExpired = (subscriptionStatus?.subscription?.expires_at && 
                               new Date(subscriptionStatus.subscription.expires_at) < new Date()) ||
                              (subscriptionStatus?.subscription?.trial_expires_at && 
                               new Date(subscriptionStatus.subscription.trial_expires_at) < new Date());
              const isBlocked = subscriptionStatus?.subscription?.status === 'trial' || 
                              isExpired ||
                              subscriptionStatus?.subscription?.package_name?.toLowerCase() === 'spark';
              if (item.name === 'API Documentation' && isBlocked) {
                return (
                  <div
                    key={item.name}
                    className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                        Premium
                      </span>
                    </div>
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-33 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-whatsapp-50 text-whatsapp-700 border-r-2 border-whatsapp-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Info & Footer */}
          <div className="px-4 py-4 border-t border-gray-200 space-y-3">
            {/* User Info */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <UserCircleIcon className="h-8 w-8 text-whatsapp-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
            
            {/* Version Info */}
            <div className="flex items-center">
              <CogIcon className="h-4 w-4 text-gray-400" />
              <span className="ml-2 text-xs text-gray-500">Snwolley v1.0.0</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-whatsapp-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
        
        <main className="flex-1 p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;